import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Autocomplete,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Popover,
    Divider,
    List,
    ListItem,
    Paper,
    Grid,
    IconButton,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { publicInstance } from '../axiosInstance';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { fetchLabels, createLabel, deleteLabel, assignLabels, fetchDeviceLabels, DeviceWithLabels, Label } from './api/labels';

interface LabelManagementProps {
    open: boolean;
    onClose: () => void;
    selectedDevices: string[];
    serviceDevices: string[];
    onLabelsAssigned: () => void;
    mode: 'assign' | 'manage';
}

interface DeviceLabelsDisplay {
    box: string;
    labels: string[];
}

interface DeviceLabelAction {
    deviceBox: string;
    labelName: string;
    action: 'add' | 'remove';
}

const LabelManagement: React.FC<LabelManagementProps> = ({
    open,
    onClose,
    selectedDevices,
    serviceDevices,
    onLabelsAssigned,
    mode
}) => {
    const theme = useTheme();
    const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
    const [newLabel, setNewLabel] = useState('');
    const [selectedLabelNames, setSelectedLabelNames] = useState<string[]>([]);
    const [currentDeviceLabels, setCurrentDeviceLabels] = useState<string[]>([]);
    const [deviceLabelsList, setDeviceLabelsList] = useState<DeviceLabelsDisplay[]>([]);
    const [labelActions, setLabelActions] = useState<DeviceLabelAction[]>([]);
    const [globalSelectedLabels, setGlobalSelectedLabels] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingCurrentLabels, setIsFetchingCurrentLabels] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [newLabelColor, setNewLabelColor] = useState('#4287f5');
    const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [applyToAll, setApplyToAll] = useState(true);

    // Map of label names to colors for quick lookup
    const [labelColorsMap, setLabelColorsMap] = useState<Record<string, string>>({});

    // Fetch existing labels
    useEffect(() => {
        if (open) {
            loadLabels();
            if (mode === 'assign' && selectedDevices.length > 0) {
                fetchCurrentDeviceLabels();
            }
        }
    }, [open, selectedDevices, mode]);

    // Build color map when availableLabels change
    useEffect(() => {
        const colorMap: Record<string, string> = {};
        availableLabels.forEach(label => {
            if (typeof label.label === 'string' && label.color) {
                colorMap[label.label] = label.color;
            }
        });
        setLabelColorsMap(colorMap);
    }, [availableLabels]);

    const loadLabels = async () => {
        try {
            setIsLoading(true);
            const fetchedLabels = await fetchLabels();
            
            // Make sure all label objects have string values for label property
            const validatedLabels = fetchedLabels.filter(label => 
                typeof label.label === 'string' && label.label.trim() !== ''
            );
            
            setAvailableLabels(validatedLabels);
        } catch (error) {
            console.error('Error fetching labels:', error);
            setError('Failed to fetch labels');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCurrentDeviceLabels = async () => {
        if (selectedDevices.length === 0) return;
        
        try {
            setIsFetchingCurrentLabels(true);
            const deviceLabelsData = await fetchDeviceLabels(selectedDevices);
            
            // Process data for individual device display
            const deviceList: DeviceLabelsDisplay[] = deviceLabelsData.map(device => {
                const boxStr = String(device.box);
                const labels: string[] = [];
                
                if (device.label && Array.isArray(device.label)) {
                    device.label.forEach(label => {
                        // Ensure label is a string
                        const labelStr = typeof label === 'string' ? label : String(label);
                        if (labelStr && !labels.includes(labelStr)) {
                            labels.push(labelStr);
                        }
                    });
                }
                
                return {
                    box: boxStr,
                    labels
                };
            });
            
            setDeviceLabelsList(deviceList);
            
            // Extract all unique labels from the devices for the selected labels list
            const allCurrentLabels = deviceLabelsData.reduce<string[]>((acc, device) => {
                if (device.label && Array.isArray(device.label)) {
                    device.label.forEach(label => {
                        const labelStr = typeof label === 'string' ? label : String(label);
                        if (labelStr && !acc.includes(labelStr)) {
                            acc.push(labelStr);
                        }
                    });
                }
                return acc;
            }, []);
            
            setCurrentDeviceLabels(allCurrentLabels);
            // Pre-select current labels
            setSelectedLabelNames(allCurrentLabels);
        } catch (error) {
            console.error('Error fetching current device labels:', error);
            setError('Failed to fetch current device labels');
        } finally {
            setIsFetchingCurrentLabels(false);
        }
    };

    const handleCreateLabel = async () => {
        if (!newLabel.trim()) return;

        try {
            setIsLoading(true);
            await createLabel(newLabel.trim(), newLabelColor);
            setSuccess('Label created successfully');
            setNewLabel('');
            setNewLabelColor('#4287f5'); // Reset color to default
            await loadLabels();
        } catch (error) {
            console.error('Error creating label:', error);
            setError('Failed to create label');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteLabel = async (labelName: string) => {
        try {
            setIsLoading(true);
            await deleteLabel(labelName);
            setSuccess('Label deleted successfully');
            await loadLabels();
        } catch (error) {
            console.error('Error deleting label:', error);
            setError('Failed to delete label');
        } finally {
            setIsLoading(false);
        }
    };

    // Get final labels for a device after applying all actions
    const getFinalLabelsForDevice = (deviceBox: string) => {
        // Start with current labels for the device
        const device = deviceLabelsList.find(d => d.box === deviceBox);
        let currentLabels = device ? [...device.labels] : [];
        
        // Apply all actions for this device
        labelActions.forEach(action => {
            if (action.deviceBox === deviceBox) {
                if (action.action === 'add' && !currentLabels.includes(action.labelName)) {
                    currentLabels.push(action.labelName);
                } else if (action.action === 'remove') {
                    currentLabels = currentLabels.filter(l => l !== action.labelName);
                }
            }
        });
        
        return currentLabels;
    };
    
    // Check if a device has a specific label after applying all actions
    const deviceHasLabelAfterActions = (deviceBox: string, labelName: string) => {
        return getFinalLabelsForDevice(deviceBox).includes(labelName);
    };

    // Handles toggling a label for a specific device
    const handleDeviceLabelToggle = (deviceBox: string, labelName: string) => {
        const willHaveLabel = !deviceHasLabelAfterActions(deviceBox, labelName);
        
        // Remove any existing actions for this device-label combo
        const filteredActions = labelActions.filter(
            action => !(action.deviceBox === deviceBox && action.labelName === labelName)
        );
        
        // Determine if we need to add or remove this label
        const device = deviceLabelsList.find(d => d.box === deviceBox);
        const hasLabelOriginally = device?.labels.includes(labelName) || false;
        
        // Only add an action if it changes the original state
        if (willHaveLabel !== hasLabelOriginally) {
            filteredActions.push({
                deviceBox,
                labelName,
                action: willHaveLabel ? 'add' : 'remove'
            });
        }
        
        setLabelActions(filteredActions);
    };

    // Handle adding a label to all devices
    const handleAddLabelToAll = (labelName: string) => {
        const newActions = [...labelActions];
        
        deviceLabelsList.forEach(device => {
            const hasLabelOriginally = device.labels.includes(labelName);
            
            // Remove any existing actions for this device-label
            const deviceIndex = newActions.findIndex(
                action => action.deviceBox === device.box && action.labelName === labelName
            );
            if (deviceIndex !== -1) {
                newActions.splice(deviceIndex, 1);
            }
            
            // Only add action if device doesn't already have the label
            if (!hasLabelOriginally) {
                newActions.push({
                    deviceBox: device.box,
                    labelName,
                    action: 'add'
                });
            }
        });
        
        setLabelActions(newActions);
    };
    
    // Handle removing a label from all devices
    const handleRemoveLabelFromAll = (labelName: string) => {
        const newActions = [...labelActions];
        
        deviceLabelsList.forEach(device => {
            const hasLabelOriginally = device.labels.includes(labelName);
            
            // Remove any existing actions for this device-label
            const deviceIndex = newActions.findIndex(
                action => action.deviceBox === device.box && action.labelName === labelName
            );
            if (deviceIndex !== -1) {
                newActions.splice(deviceIndex, 1);
            }
            
            // Only add remove action if device originally had the label
            if (hasLabelOriginally) {
                newActions.push({
                    deviceBox: device.box,
                    labelName,
                    action: 'remove'
                });
            }
        });
        
        setLabelActions(newActions);
    };

    // Toggle a label globally (affecting all or selected devices)
    const handleGlobalLabelToggle = (labelName: string) => {
        // Update the global selected labels list
        const isCurrentlySelected = globalSelectedLabels.includes(labelName);
        
        if (isCurrentlySelected) {
            setGlobalSelectedLabels(prev => prev.filter(l => l !== labelName));
            
            if (applyToAll) {
                handleRemoveLabelFromAll(labelName);
            }
        } else {
            setGlobalSelectedLabels(prev => [...prev, labelName]);
            
            if (applyToAll) {
                handleAddLabelToAll(labelName);
            }
        }
    };

    const handleAssignLabels = async () => {
        if (selectedDevices.length === 0) return;

        try {
            setIsLoading(true);
            
            // Apply individual actions for each device
            const devicePromises = deviceLabelsList.map(async (device) => {
                const finalLabels = getFinalLabelsForDevice(device.box);
                const isServiceDevice = serviceDevices.includes(device.box);
                const endpoint = isServiceDevice ? '/dacapi_inventory_temp' : '/dacapi_inventory_current';
                
                await publicInstance.post(endpoint, {
                    box: parseInt(device.box),
                    label: finalLabels.length > 0 ? finalLabels : null
                }, {
                    headers: {
                        'Prefer': 'resolution=merge-duplicates'
                    }
                });
            });
            
            await Promise.all(devicePromises);
            
            setSuccess('Labels assigned successfully');
            onLabelsAssigned();
            onClose();
        } catch (error) {
            console.error('Error assigning labels:', error);
            setError('Failed to assign labels');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveAllLabels = async () => {
        if (selectedDevices.length === 0) return;

        try {
            setIsLoading(true);
            await assignLabels(selectedDevices, [], serviceDevices, true); // Empty array will remove all labels
            setSuccess('Labels removed successfully');
            onLabelsAssigned();
            onClose();
        } catch (error) {
            console.error('Error removing labels:', error);
            setError('Failed to remove labels');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedLabelNames([]);
        setCurrentDeviceLabels([]);
        setDeviceLabelsList([]);
        setLabelActions([]);
        setGlobalSelectedLabels([]);
        setError(null);
        setSuccess(null);
        onClose();
    };

    const handleColorPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColorPickerAnchorEl(event.currentTarget);
    };

    const handleColorPickerClose = () => {
        setColorPickerAnchorEl(null);
    };

    // Render a label chip with proper styling
    const renderLabelChip = (labelName: string, size: 'small' | 'medium' = 'medium', onClick?: () => void) => {
        const color = labelColorsMap[labelName] || '#4287f5';
        
        return (
            <Chip
                key={labelName}
                label={labelName}
                size={size}
                onClick={onClick}
                sx={{
                    backgroundColor: color,
                    color: theme.palette.getContrastText(color),
                    fontWeight: 'medium',
                    '&:hover': {
                        backgroundColor: color,
                        opacity: 0.9
                    }
                }}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
        >
            <DialogTitle>
                {mode === 'assign' ? 'Manage Device Labels' : 'Manage Labels Collection'}
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {mode === 'manage' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Create New Label
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                value={newLabel}
                                onChange={(e) => setNewLabel(e.target.value)}
                                placeholder="Enter new label"
                                size="small"
                                fullWidth
                            />
                            <Button
                                variant="outlined"
                                onClick={handleColorPickerOpen}
                                sx={{
                                    width: '40px',
                                    height: '40px',
                                    minWidth: '40px',
                                    padding: 0,
                                    backgroundColor: newLabelColor,
                                    borderColor: newLabelColor,
                                    '&:hover': {
                                        backgroundColor: newLabelColor,
                                        borderColor: newLabelColor,
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleCreateLabel}
                                disabled={!newLabel.trim() || isLoading}
                            >
                                Create
                            </Button>
                        </Box>
                        <Popover
                            open={Boolean(colorPickerAnchorEl)}
                            anchorEl={colorPickerAnchorEl}
                            onClose={handleColorPickerClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                                <ChromePicker
                                    color={newLabelColor}
                                onChange={(color) => setNewLabelColor(color.hex)}
                            />
                        </Popover>
                    </Box>
                )}

                {mode === 'assign' && (
                    <Grid container spacing={2}>
                        {/* Left panel - Available Labels */}
                        <Grid item xs={3}>
                            <Paper elevation={0} variant="outlined" sx={{ height: '100%', p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Available Labels
                </Typography>

                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={applyToAll} 
                                            onChange={(e) => setApplyToAll(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Apply to all devices"
                                    sx={{ mb: 2, display: 'block' }}
                                />

                    {isLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                                    </Box>
                                ) : availableLabels.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No labels available
                        </Typography>
                    ) : (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        gap: 1,
                                        maxHeight: 'calc(90vh - 300px)',
                                        overflowY: 'auto',
                                    }}>
                                        {availableLabels.map((labelObj) => {
                                            const labelName = typeof labelObj.label === 'string' ? 
                                                labelObj.label : String(labelObj.label);
                                            const isSelected = globalSelectedLabels.includes(labelName);
                                            
                                            return (
                                                <Box 
                                                    key={labelName}
                                                    sx={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        bgcolor: isSelected ? 
                                                            `${theme.palette.primary.main}10` : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: isSelected ?
                                                                `${theme.palette.primary.main}20` : 
                                                                theme.palette.action.hover
                                                        },
                                                        cursor: 'pointer',
                                                        border: isSelected ? 
                                                            `1px solid ${theme.palette.primary.main}` : 
                                                            `1px solid transparent`
                                                    }}
                                                    onClick={() => handleGlobalLabelToggle(labelName)}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                borderRadius: '50%',
                                                                backgroundColor: labelObj.color,
                                                                mr: 1
                                                            }}
                                                        />
                                                        <Typography variant="body2">
                                                            {labelName}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box>
                                                        {isSelected ? (
                                                            <CheckCircleOutlineIcon 
                                                                fontSize="small" 
                                                                color="primary"
                                                            />
                                                        ) : (
                                                            <AddCircleOutlineIcon 
                                                                fontSize="small" 
                                                                color="action"
                                                            />
                    )}
                </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        
                        {/* Right panel - Device Labels Table */}
                        <Grid item xs={9}>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, height: '100%' }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Device Labels
                                </Typography>
                                
                                {isFetchingCurrentLabels ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : deviceLabelsList.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No device information available
                                    </Typography>
                                ) : (
                                    <TableContainer sx={{ 
                                        maxHeight: 'calc(90vh - 250px)', 
                                        overflowY: 'auto',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        mt: 1
                                    }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Box Number</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', width: '80%' }}>Labels</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {deviceLabelsList.map((device) => {
                                                    const finalLabels = getFinalLabelsForDevice(device.box);
                                                    
                                                    return (
                                                        <TableRow key={device.box} hover>
                                                            <TableCell 
                                                                component="th" 
                                                                scope="row"
                                                                sx={{ fontWeight: 'medium' }}
                                                            >
                                                                {device.box}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    flexWrap: 'wrap',
                                                                    gap: 0.5
                                                                }}>
                                                                    {finalLabels.length > 0 ? (
                                                                        finalLabels.map((label) => (
                                                                            renderLabelChip(label, 'small', () => 
                                                                                handleDeviceLabelToggle(device.box, label)
                                                                            )
                                                                        ))
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            No labels
                    </Typography>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    <Tooltip title="Add labels from selection">
                                                                        <IconButton 
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => {
                                                                                // Add all globally selected labels to this device
                                                                                globalSelectedLabels.forEach(label => {
                                                                                    if (!deviceHasLabelAfterActions(device.box, label)) {
                                                                                        handleDeviceLabelToggle(device.box, label);
                                                                                    }
                                                                                });
                                                                            }}
                                                                            disabled={globalSelectedLabels.length === 0}
                                                                        >
                                                                            <AddCircleOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Clear all labels">
                                                                        <IconButton 
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => {
                                                                                // Remove all current labels
                                                                                finalLabels.forEach(label => {
                                                                                    if (deviceHasLabelAfterActions(device.box, label)) {
                                                                                        handleDeviceLabelToggle(device.box, label);
                                                                                    }
                                                                                });
                                                                            }}
                                                                            disabled={finalLabels.length === 0}
                                                                        >
                                                                            <RemoveCircleOutlineIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                {mode === 'assign' && (
                    <>
                        <Button 
                            onClick={handleRemoveAllLabels}
                            variant="outlined"
                            color="error"
                            disabled={selectedDevices.length === 0 || isLoading}
                        >
                            Remove All Labels
                        </Button>
                        <Button 
                            onClick={handleAssignLabels}
                            variant="contained"
                            disabled={selectedDevices.length === 0 || isLoading}
                        >
                            Apply Changes
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LabelManagement; 